/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// Kept separate from cardOverflow so sign-out teardown can clear the overlay
// without static-importing the overflow menu (which drags contextMenu /
// ReportSheet / ShareModal / entityNav onto the eager boot path).

import { createPersistentItem } from '@amazon/vinyl-util'
import { createLocalOverlay } from '@/util/localOverlay'

export type FollowState = 'following' | 'not-following'

// Per-entity local follow flag, keyed by artist / playlist id, so an
// optimistic follow survives to the next menu open without a Firefly
// round-trip. TTL is long because this is durable on-device state, not a
// short server-catch-up window.
const FOLLOW_OVERLAY_TTL_MS = 30 * 24 * 60 * 60 * 1000
const followOverlay = createLocalOverlay<string, boolean>({
    persistence: createPersistentItem({
        key: 'overlay:follow',
        defaultValue: {},
    }),
    validateValue: (v): v is boolean => typeof v === 'boolean',
})

/**
 * Drop every remembered follow flag on sign-out. The in-memory overlay
 * survives the `localStorage` wipe, so an explicit `clearAll` is required
 * to stop one account's follow state bleeding into the next.
 */
export function clearFollowStateOverlay(): void {
    followOverlay.clearAll()
}

export function readFollowState(entityId: string): FollowState {
    return followOverlay.read(entityId) === true ? 'following' : 'not-following'
}

export function writeFollowState(entityId: string, state: FollowState): void {
    if (state === 'following') {
        followOverlay.write(entityId, true, { ttlMs: FOLLOW_OVERLAY_TTL_MS })
    } else {
        followOverlay.clear(entityId)
    }
}
